// Cockpit revamp — campaign audit hook.
//
// Reads the audit log via the existing `/campaigns/:id/audit` endpoint
// and refreshes after a write (transition / inline edit). The cockpit
// pages append-only — entries are server-generated, not client-side.
import { useCallback, useEffect, useState } from 'react';

import {
  campaignsAPI,
  extractCampaignApiError,
} from '../../../services/campaignsAPI';
import type { CampaignAuditLogEntry } from '../../../types/campaign';

const AUDIT_PAGE_SIZE = 50;

export interface UseCampaignAuditResult {
  audit: CampaignAuditLogEntry[];
  loading: boolean;
  error: string | null;
  /** Re-fetch the audit log. Call after any audited write (transition,
   * inline AI-context edit, KPI target edit). */
  refresh: () => Promise<void>;
}

export function useCampaignAudit(
  campaignId: string | undefined,
): UseCampaignAuditResult {
  const [audit, setAudit] = useState<CampaignAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const rows = await campaignsAPI.auditLog(campaignId, {
        limit: AUDIT_PAGE_SIZE,
      });
      setAudit(rows);
      setError(null);
    } catch (e) {
      setError(extractCampaignApiError(e, 'Failed to load audit log.'));
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    refresh().catch(() => undefined);
  }, [refresh]);

  return { audit, loading, error, refresh };
}
